import React, { useCallback, useEffect, useState } from "react"

import { Loader2, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

const DriverManagerTeams: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rows, setRows] = useState<any[]>([])
  const [search, setSearch] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [syncRunning, setSyncRunning] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all")

  const fetchData = useCallback(async () => {
    setError(null)

    try {
      let query = supabase
        .from("driver_manager_teams")
        .select("id, name, team, airtable_record_id, active")

      if (search.trim()) {
        const s = `%${search}%`
        query = query.or(`name.ilike.${s},team.ilike.${s},airtable_record_id.ilike.${s}`)
      }

      // Apply active filter
      if (activeFilter === "active") {
        query = query.eq("active", true)
      } else if (activeFilter === "inactive") {
        query = query.eq("active", false)
      }
      // "all" means no filter

      const { data, error } = await query.order("active", { ascending: false }).order("name")
      if (error) throw error

      setRows(data ?? [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message)
    }
  }, [search, activeFilter])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const runSync = async () => {
    if (syncRunning) return
    setSyncRunning(true)
    setSyncMessage(null)

    try {
      await supabase.functions.invoke("airtable-sync-driver-manager-teams")
      setSyncMessage("Sync completed")
      await fetchData()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setSyncMessage(`Sync failed: ${e.message}`)
    } finally {
      setSyncRunning(false)
    }
  }

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      // Optimistic update
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === id ? { ...row, active: !currentActive } : row)),
      )

      const { error } = await supabase
        .from("driver_manager_teams")
        .update({ active: !currentActive })
        .eq("id", id)

      if (error) {
        // Revert on error
        setRows((prevRows) =>
          prevRows.map((row) => (row.id === id ? { ...row, active: currentActive } : row)),
        )
        throw error
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(`Failed to toggle active: ${e.message}`)
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-[95vw] mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Driver Manager Teams</h1>
          <p className="text-muted-foreground">
            Browse Driver Manager Teams. Sync to fetch latest from Airtable.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardDescription>Showing: {rows.length}</CardDescription>

            <div className="mt-3 flex gap-3 items-center flex-wrap">
              <div className="relative w-64">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-md border pl-8 pr-2 py-1 bg-background"
                />
              </div>

              <Select
                value={activeFilter}
                onValueChange={(value: "all" | "active" | "inactive") => setActiveFilter(value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={runSync} disabled={syncRunning}>
                {syncRunning ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Syncing…
                  </span>
                ) : (
                  "Sync from Airtable"
                )}
              </Button>

              {syncMessage && <div className="text-sm">{syncMessage}</div>}
            </div>
          </CardHeader>

          <CardContent>
            {error ? (
              <div className="text-red-600">{error}</div>
            ) : (
              <div className="w-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Manager Name</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Airtable Record ID</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={idx} className={row.active ? "" : "opacity-50"}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.team}</TableCell>
                        <TableCell>{row.airtable_record_id}</TableCell>
                        <TableCell>
                          <Switch
                            checked={Boolean(row.active)}
                            onCheckedChange={() =>
                              toggleActive(String(row.id), Boolean(row.active))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default DriverManagerTeams
